import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { env } from '@/config/env';

interface FlagWriteConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  /** True when the API this build talks to isn't recognisably non-prod. */
  prodGuard: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation for a write from one of the flag sub-resource panels
 * (overrides, identity rules).
 *
 * The pages already gate every flag toggle on `requiresConfirmation`, but the
 * panels used to write straight through — so on the prod dashboard, turning a
 * flag off warned you and narrowing the same flag to a platform, or aiming it
 * at a set of people, did not. Both reach the same live users; both should
 * pause first.
 */
export function FlagWriteConfirmDialog({
  open,
  title,
  description,
  prodGuard,
  busy,
  onConfirm,
  onCancel,
}: FlagWriteConfirmDialogProps) {
  return (
    <AlertDialog
      open={open}
      onOpenChange={next => {
        if (!next) onCancel();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>
            {description}
            {prodGuard && (
              <span className="mt-2 block">
                You are making this change against{' '}
                <code className="font-mono">{env.VITE_API_URL}</code>. This URL is not recognized
                as a non-prod environment, so the change may affect live users immediately.
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy}
            onClick={event => {
              // Hold the dialog open until the write settles, as the pages do.
              event.preventDefault();
              onConfirm();
            }}
          >
            Confirm
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
